import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, Clock, XCircle, ShoppingCart, Home } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_0890dc80-6146-4c2d-b11f-fa0b2de6d70b/artifacts/ekzdqal8_image.png";
const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p);

const statusConfig = {
  success: { icon: CheckCircle, color: "text-green-400", bg: "bg-green-400/10", title: "Pembayaran Berhasil!", desc: "Terima kasih! Pembayaran QRIS Anda telah dikonfirmasi." },
  pending: { icon: Clock, color: "text-yellow-400", bg: "bg-yellow-400/10", title: "Menunggu Pembayaran", desc: "Pembayaran Anda sedang diproses. Status akan diperbarui otomatis." },
  failed: { icon: XCircle, color: "text-red-400", bg: "bg-red-400/10", title: "Pembayaran Gagal", desc: "Pembayaran tidak berhasil. Silakan coba lagi atau hubungi admin." },
};

export default function PaymentStatusPage({ status = "pending" }) {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id") || "";
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(status === "pending");

  useEffect(() => {
    if (orderId) fetchOrder();
    else setLoading(false);
  }, [orderId]);

  // Poll for status changes if pending
  useEffect(() => {
    if (!polling || !orderId) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/order-status/${orderId}`);
        setOrder(data);
        if (data.payment_status === "paid" || data.payment_status === "failed") {
          setPolling(false);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [polling, orderId]);

  const fetchOrder = async () => {
    try { const { data } = await api.get(`/payments/order-status/${orderId}`); setOrder(data); } catch {} finally { setLoading(false); }
  };

  const effectiveStatus = order?.payment_status === "paid" ? "success" : order?.payment_status === "failed" ? "failed" : status;
  const config = statusConfig[effectiveStatus] || statusConfig.pending;
  const Icon = config.icon;

  if (loading) return <div className="flex items-center justify-center h-screen bg-[#050505]"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6" data-testid={`payment-${effectiveStatus}`}>
      <div className="max-w-md text-center">
        <img src={LOGO} alt="FI" className="w-14 h-14 mx-auto mb-6 rounded-full" />
        <div className={`w-20 h-20 mx-auto mb-6 ${config.bg} flex items-center justify-center`}>
          <Icon size={40} className={config.color} />
        </div>
        <h1 className="font-heading text-3xl font-bold uppercase mb-2">{config.title}</h1>
        <p className="text-gray-400 mb-6">{config.desc}</p>

        {order && (
          <div className="bg-[#0F0F0F] border border-[#262626] p-6 text-left mb-6">
            <div className="space-y-2">
              <div className="flex justify-between"><span className="text-gray-400 text-sm">Kode Pesanan</span><span className="font-mono text-[#E60000] text-sm font-bold">{order.order_code}</span></div>
              <div className="flex justify-between"><span className="text-gray-400 text-sm">Total</span><span className="text-white font-semibold">{formatPrice(order.total)}</span></div>
              <div className="flex justify-between"><span className="text-gray-400 text-sm">Status Bayar</span><span className={`text-sm font-semibold ${order.payment_status === "paid" ? "text-green-400" : order.payment_status === "failed" ? "text-red-400" : "text-yellow-400"}`}>{order.payment_status?.toUpperCase()}</span></div>
              <div className="flex justify-between"><span className="text-gray-400 text-sm">Status Pesanan</span><span className="text-white text-sm">{order.order_status}</span></div>
            </div>
          </div>
        )}

        {effectiveStatus === "pending" && polling && (
          <div className="flex items-center justify-center gap-2 text-yellow-400 text-sm mb-6">
            <div className="w-3 h-3 border border-yellow-400 border-t-transparent rounded-full animate-spin" />
            Menunggu konfirmasi pembayaran...
          </div>
        )}

        <div className="flex gap-3">
          <Link to="/toko" className="flex-1"><Button className="w-full bg-[#262626] hover:bg-[#333] text-white rounded-none h-10 font-bold uppercase text-xs"><ShoppingCart size={14} className="mr-2" />Lanjut Belanja</Button></Link>
          <Link to="/portal" className="flex-1"><Button className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white rounded-none h-10 font-bold uppercase text-xs">Lihat Pesanan</Button></Link>
        </div>

        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-white mt-6"><Home size={14} />Kembali ke Beranda</Link>
      </div>
    </div>
  );
}
